import { c as _c } from "react/compiler-runtime";
import { BREAKPOINTS } from '@hookdeck/theme';
import { useContext, useEffect, useRef, useState } from 'react';
import styled, { css, useTheme } from 'styled-components';
import Button from '../../../common/base/Button';
import Text from '../../../common/base/Text';
import { Div } from '../../../common/helpers/StyledUtils';
import ChartInteractionGroup from '../../../common/Chart/ChartInteractionGroup';
import { MetricName } from '../../../common/metrics/metric-definitions';
import { RelativeDate } from '../../../../utils/date';
import MetricChart from '../Metrics/MetricChart';
import useSearchQuery from '../../../hooks/useSearchQuery';
import useViewportBreakpoint from '../../../hooks/useViewportBreakpoint';
import { metric_categories } from '../Metrics/MetricCategory';
import MetricDatePicker, { useDateRange } from '../Metrics/MetricDatePicker';
import { useRefreshKey } from '../Metrics/RefreshKeyContext';
import { DashboardContext } from '../DashboardContext';
import { VIEW_NAV_HEIGHT } from '../../../layouts/ViewLayout';
const columns_for_breakpoints: Record<keyof typeof BREAKPOINTS, number> = {
  mobile: 1,
  tablet: 2,
  laptop: 2,
  desktop: 3
};
const METRICS_HEADER_HEIGHT = 52;
const EXPANDED_CHART_VERTICAL_SPACING = 6;
const METRICS_HEADER_SECTION_TITLES: Record<string, string> = {
  Events: 'Event'
};
const METRICS_HEADER_RESOURCE_TITLES: Partial<Record<keyof typeof metric_categories, string>> = {
  sources: 'Source metrics',
  connections: 'Connection metrics'
};
const getMetricsSectionTitle = (section_title: string) => `${METRICS_HEADER_SECTION_TITLES[section_title] || section_title} metrics`;
const StyledResourceMetricsSection = styled.div<{
  $expanded: boolean;
}>`
  padding-top: ${({
  theme,
  $expanded
}) => $expanded ? 0 : theme.spacing(2)};
`;
const StyledMetricsHeader = styled(Div)<{
  sticky_top: number;
  is_sticking: boolean;
}>(({
  theme,
  sticky_top,
  is_sticking
}) => css`
    position: sticky;
    top: ${theme.pxToRem(sticky_top)};
    z-index: ${theme.zindex.sticky};
    background-color: ${theme.colors.surface.base.surface};
    padding-top: ${theme.spacing(3)};
    padding-bottom: ${theme.spacing(3)};
    padding-left: ${theme.spacing(6)};
    padding-right: ${theme.spacing(6)};
    /* Bleed through the container's horizontal padding. */
    margin-left: -${theme.spacing(6)};
    margin-right: -${theme.spacing(6)};
    ${is_sticking && css`
      border-bottom: ${theme.border};
    `}
  `);
const ResourceMetricSection: React.FC<{
  id: string;
  resource: keyof typeof metric_categories;
  default_relative_date?: RelativeDate;
}> = t0 => {
  const $ = _c(47);
  const {
    id,
    resource,
    default_relative_date
  } = t0;
  const theme = useTheme();
  const breakpoint = useViewportBreakpoint();
  const {
    updateRefreshKey
  } = useRefreshKey();
  const {
    is_degraded_banner_active
  } = useContext(DashboardContext);
  const metric_category = metric_categories[resource];
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const section_refs = useRef(t1);
  const header_ref = useRef(null);
  const [active_section_index, setActiveSectionIndex] = useState(0);
  const [is_sticking, setIsSticking] = useState(false);
  const {
    query,
    updateSearchQuery
  } = useSearchQuery();
  const [date_range, onDateRangeChanged] = useDateRange(default_relative_date);
  const sticky_top = is_degraded_banner_active ? VIEW_NAV_HEIGHT + 32 : VIEW_NAV_HEIGHT;
  const active_section_title = metric_category.sections[active_section_index]?.title;
  let t2;
  if ($[1] !== active_section_title || $[2] !== resource) {
    t2 = active_section_title ? getMetricsSectionTitle(active_section_title) : METRICS_HEADER_RESOURCE_TITLES[resource] || "Metrics";
    $[1] = active_section_title;
    $[2] = resource;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const title = t2;
  const expanded_chart_height = typeof window === "undefined" ? undefined : window.innerHeight - sticky_top - METRICS_HEADER_HEIGHT - theme.grid_size * EXPANDED_CHART_VERTICAL_SPACING * 2;
  let t3;
  if ($[4] !== query.expanded || $[5] !== sticky_top) {
    t3 = () => {
      if (query.expanded) {
        return;
      }
      const updateActiveSection = () => {
        const header_top = header_ref.current?.getBoundingClientRect().top;
        if (header_top !== undefined) {
          setIsSticking(Math.abs(header_top - sticky_top) < 1);
        }
        const threshold = sticky_top + 56;
        const active_index_0 = section_refs.current.reduce((active_index, section, index) => {
          if (!section) {
            return active_index;
          }
          return section.getBoundingClientRect().top <= threshold ? index : active_index;
        }, 0);
        setActiveSectionIndex(active_index_0);
      };
      updateActiveSection();
      window.addEventListener("scroll", updateActiveSection, {
        passive: true
      });
      window.addEventListener("resize", updateActiveSection);
      return () => {
        window.removeEventListener("scroll", updateActiveSection);
        window.removeEventListener("resize", updateActiveSection);
      };
    };
    $[4] = query.expanded;
    $[5] = sticky_top;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== metric_category.sections || $[8] !== query.expanded || $[9] !== sticky_top) {
    t4 = [metric_category.sections, query.expanded, sticky_top];
    $[7] = metric_category.sections;
    $[8] = query.expanded;
    $[9] = sticky_top;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  useEffect(t3, t4);
  const t5 = !!query.expanded;
  const t6 = is_sticking || !!query.expanded;
  let t7;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      justify: "space-between",
      align: "center"
    };
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      align: "center"
    };
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== title) {
    t9 = <Text as="h2" size="l" bold={true} m={0} flex={t8}>{title}</Text>;
    $[13] = title;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = {
      gap: 3
    };
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  let t11;
  if ($[16] !== date_range || $[17] !== onDateRangeChanged) {
    t11 = <MetricDatePicker date_range={date_range} onDateRangeChanged={onDateRangeChanged} />;
    $[16] = date_range;
    $[17] = onDateRangeChanged;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  let t12;
  if ($[19] !== updateRefreshKey) {
    t12 = <Button outline={true} icon="reload" onClick={() => updateRefreshKey()}>Refresh</Button>;
    $[19] = updateRefreshKey;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  let t13;
  if ($[21] !== t11 || $[22] !== t12) {
    t13 = <Div flex={t10}>{t11}{t12}</Div>;
    $[21] = t11;
    $[22] = t12;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  let t14;
  if ($[24] !== sticky_top || $[25] !== t13 || $[26] !== t6 || $[27] !== t9) {
    t14 = <StyledMetricsHeader ref={header_ref} sticky_top={sticky_top} is_sticking={t6} flex={t7}>{t9}{t13}</StyledMetricsHeader>;
    $[24] = sticky_top;
    $[25] = t13;
    $[26] = t6;
    $[27] = t9;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  let t15;
  if ($[29] !== onDateRangeChanged) {
    t15 = range => onDateRangeChanged({
      relative: undefined,
      min: range.min,
      max: range.max
    });
    $[29] = onDateRangeChanged;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  let t16;
  if ($[31] !== breakpoint || $[32] !== date_range || $[33] !== expanded_chart_height || $[34] !== id || $[35] !== metric_category.dimension || $[36] !== metric_category.sections || $[37] !== query.expanded || $[38] !== updateSearchQuery) {
    t16 = query.expanded ? <Div p={{
      t: EXPANDED_CHART_VERTICAL_SPACING
    }}><MetricChart height={expanded_chart_height} metric={query.expanded} onExpand={() => {
        updateSearchQuery({
          expanded: undefined
        });
      }} expanded={true} configs={{
        date_range,
        options: {
          filters: {
            [metric_category.dimension.key]: [id]
          }
        }
      }} /></Div> : <Div m={{
      b: 20
    }}>{metric_category.sections.map((section_0, i) => <Div key={section_0.title || i} ref={element => {
        section_refs.current[i] = element;
      }}>{i !== 0 && section_0.title && <Text as="h2" size="l" bold={true} m={{
          t: 12,
          b: 4
        }}>{getMetricsSectionTitle(section_0.title)}</Text>}{section_0.charts && <Div grid={{
          columns: columns_for_breakpoints[breakpoint],
          gap: 6
        }} p={{
          t: i === 0 ? 1 : 0
        }}>{section_0.charts.map(metric => <MetricChart key={metric} metric={metric} configs={{
            date_range,
            options: {
              filters: {
                [metric_category.dimension.key]: [id]
              }
            }
          }} onExpand={() => {
            updateSearchQuery({
              expanded: metric
            });
          }} />)}</Div>}</Div>)}</Div>;
    $[31] = breakpoint;
    $[32] = date_range;
    $[33] = expanded_chart_height;
    $[34] = id;
    $[35] = metric_category.dimension;
    $[36] = metric_category.sections;
    $[37] = query.expanded;
    $[38] = updateSearchQuery;
    $[39] = t16;
  } else {
    t16 = $[39];
  }
  let t17;
  if ($[40] !== t15 || $[41] !== t16) {
    t17 = <ChartInteractionGroup onTimeRangeSelected={t15}>{t16}</ChartInteractionGroup>;
    $[40] = t15;
    $[41] = t16;
    $[42] = t17;
  } else {
    t17 = $[42];
  }
  let t18;
  if ($[43] !== t14 || $[44] !== t17 || $[45] !== t5) {
    t18 = <StyledResourceMetricsSection $expanded={t5}>{t14}{t17}</StyledResourceMetricsSection>;
    $[43] = t14;
    $[44] = t17;
    $[45] = t5;
    $[46] = t18;
  } else {
    t18 = $[46];
  }
  return t18;
};
export default ResourceMetricSection;